'use client';
import React, { useState, useRef, useEffect } from 'react'
import gsap from 'gsap';
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { CheckIcon, XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { getPlaneKeyframes } from "@/lib/getPlaneKeyframes";
import { getTrailsKeyframes } from "@/lib/getTrailsKeyframes";
import { NewsletterCopy } from '@/app/copywritting';

interface Props {
  containerRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  height?: number;
}

const NewsletterForm = (props: Props) => {
  const { containerRef } = props;
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrollColorChange, setScrollColorChange] = useState(false);
  const [inputData, setInputData ] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [active, setActive] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { to, fromTo, set } = gsap;

  const handleSubmit = async (e:React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const email = inputData;
    const button = buttonRef.current;

    if (!email || !button) return;

    if (!active) {
      setActive(true);

      to(button, {
        keyframes: getPlaneKeyframes(set, fromTo, button, setActive, setInputData),
      });

      to(button, { keyframes: getTrailsKeyframes(button) });
    }
    const res = await fetch("/api/mailchimpSubscription", {
      body: JSON.stringify({ email }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
    const data = await res.json();
    

    if (data.error) {
      setErrorMessage("Hey, you are already subscribed!");
      setSuccessMessage("");
      return;
    }
    setSuccessMessage(data.res);
    setErrorMessage("");
  }

  const dismissMessages = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  useEffect(() => {
    const handleScroll = () => {
      const target = containerRef?.current || document.documentElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const totalHeight = scrollHeight - clientHeight;
      const progress = (scrollTop / totalHeight) * 100;
      if (progress >= 94) {
        setScrollColorChange(true)
      } else {
        setScrollColorChange(false)
      }

    }
    window.addEventListener("scroll", handleScroll)
    return () => {window.removeEventListener("scroll", handleScroll)}
  }, [])

  return (
    <section className='h-100 flex flex-col place-items-center py-10'>
      <div className='w-full flex flex-col place-items-center pb-1'>
            <h3 className="text-4xl md:text-big-heading text-center font-bold uppercase tracking-tighter pb-6">{NewsletterCopy.title}</h3>
        <p className={`w-full md:w-[60%] text-[#6d6e73] md:text-[#f8f8ff] text-medium md:text-xl text-center px-5 leading-none md:leading-subheading transition-colors duration-500 ease-in-out ${scrollColorChange ? "md:text-black" : "md:text-white"}`}>{NewsletterCopy.subtitle}</p>
        </div>

      <div className="flex flex-col space-y-8 md:w-[400px]">
        <form
          onSubmit={handleSubmit}
          className="newsletter-form mt-10 animate-fade-in-3"
        >
          <div className="group flex items-center gap-x-4 py-1 pl-4 pr-1 rounded-[9px] bg-[#ffffff] hover:bg-[#15141B] shadow-outline-gray hover:shadow-transparent focus-within:bg-[#15141B] focus-within:!shadow-outline-gray-focus transition-all duration-300">
            <EnvelopeIcon className="hidden sm:inline w-6 h-6 text-[#4B4C52] group-focus-within:text-white group-hover:text-white transition-colors duration-300" />
            <input
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              placeholder="Enter your Email"
              required
              type="email"
              className="flex-1 text-white text-sm sm:text-base outline-none placeholder-[#4B4C52] group-focus-within:placeholder-white bg-transparent placeholder:transition-colors placeholder:duration-300"
            />
            <button
              ref={buttonRef}
              className={`${
                active && "active"
              } disabled:!bg-[#17141F] disabled:grayscale-[65%] disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base`}
              disabled={!inputData}
              type="submit"
            >
              <span className="default">{NewsletterCopy.cta_text}</span>
              <span className="success">
                <svg viewBox="0 0 16 16">
                  <polyline points="3.75 9 7 12 13 5"></polyline>
                </svg>
                Done
              </span>
              <svg className="trails" viewBox="0 0 33 64">
                <path d="M26,4 C28,13.3333333 29,22.6666667 29,32 C29,41.3333333 28,50.6666667 26,60"></path>
                <path d="M6,4 C8,13.3333333 9,22.6666667 9,32 C9,41.3333333 8,50.6666667 6,60"></path>
              </svg>
              <div className="plane">
                <div className="left"></div>
                <div className="right"></div>
              </div>
            </button>
          </div>
        </form>

        <div className="relative">
          {(successMessage || errorMessage) && (
            <div className="flex items-start space-x-2 bg-[#ffffff] shadow-outline-gray text-white rounded-[9px] py-4 px-6 animate-fade-bottom absolute">
              <div className="h-6 w-6 bg-[#00ff00] flex items-center justify-center rounded-full border border-[#00ff00] flex-shrink-0">
                {/* <CheckIcon className="h-4 w-4 text-[#81A89A]" /> */}
                <FontAwesomeIcon icon={faCheck} className="fa-fw h-4 w-4" size="1x" />
              </div>
              <div className="text-xs sm:text-sm text-[#4B4C52]">
                {successMessage ? (
                  <p>
                    We&apos;ve added{" "}
                    <span className="text-[#ADB0B1]">
                      {inputData}
                    </span>{" "}
                    to our newsletter. Start enjoying our awesome content!
                  </p>
                ) : (
                  <p>
                    You are already added to our newsletter. Keep enjoying our awesome content!
                  </p>
                )}
              </div>
              <XMarkIcon
                className="h-5 w-5 cursor-pointer flex-shrink-0 text-[#4A4B55] font-bold"
                onClick={dismissMessages}
              />
            </div>
          )}
        </div>
    </div>
    </section>
  )
}

export default NewsletterForm